import { useEffect } from 'react';

interface ArticleJsonLdProps {
    title: string;
    description: string;
    slug: string;
    image: string;
    datePublished: string;
    dateModified?: string;
    authorName: string;
    tags?: string[];
}

const BASE_URL = 'https://antz.ai';

export function useArticleJsonLd({
    title,
    description,
    slug,
    image,
    datePublished,
    dateModified,
    authorName,
    tags = [],
}: ArticleJsonLdProps) {
    useEffect(() => {
        const isAbsoluteUrl = image.startsWith('http');
        const imageUrl = isAbsoluteUrl ? image : `${BASE_URL}${image}`;

        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: title,
            description,
            image: imageUrl,
            url: `${BASE_URL}${slug}`,
            datePublished,
            dateModified: dateModified ?? datePublished,
            author: {
                '@type': 'Organization',
                name: authorName,
                url: BASE_URL,
            },
            publisher: {
                '@type': 'Organization',
                name: 'Antz AI',
                url: BASE_URL,
                logo: {
                    '@type': 'ImageObject',
                    url: `${BASE_URL}/antz-logo.png`,
                },
            },
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `${BASE_URL}${slug}`,
            },
            keywords: tags.join(', '),
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-article-jsonld', 'true');
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, [title, description, slug, image, datePublished, dateModified, authorName, tags]);
}
